import Vue from 'vue'
import Filters from '@/models/filters'

const FILTERDATA_KEY = 'selected_filters'

// Helper functions for localStorage
function getData(key: string, query?: any) {
  // Alter values of brand/model if query is set
  if (query && ['brand', 'model'].includes(key)) {
    // If brand/model not set, reset them
    if (!query[key]) {
      return ''
    }

    // If brand/model is set and value is not valid, reset
    if (!valueExistsInOption(query[key], key, 'primary')) {
      return ''
    }
    // Query value is valid, return its value
    return query[key]
  }

  if (query && key === 'carselexy') {
    if (query[key] === '0') return
    return query[key]
  }

  // Alter values of filter options if query is set
  if (query && key === 'options') {
    const selectedOptions: any = {}
    // For each of these keys, check if they exist in the url query
    const saveOptionsFromQuery = ['body', 'fuelType', 'transmission', 'buildYear', 'buildyear', 'fueltype']
    saveOptionsFromQuery.forEach((optionsKey: string) => {
      const vehicleOptionKey = optionsKey === 'buildYear' || optionsKey === 'buildyear' ? 'year' : optionsKey
      // Go to next item
      if (!query[optionsKey]) {
        return
      }

      // Get all the values
      const rawValues = query[optionsKey].split('_')

      // Filter out values that are not a valid option
      const values = rawValues.filter((value: string) => valueExistsInOption(value, vehicleOptionKey, 'other'))

      // Save the resulting values
      if (values.length) {
        selectedOptions[vehicleOptionKey] = values
      }
    })

    return selectedOptions
  }

  // There is no query, fall back to session or default values
  if (sessionStorage.getItem(FILTERDATA_KEY)) {
    if (['brand', 'model'].includes(key)) {
      return ''
    }
    if (key === 'options') {
      return {}
    }
    const selectedFilters = JSON.parse(sessionStorage.getItem(FILTERDATA_KEY) || '')
    return selectedFilters[key]
    // If data does not exist (yet), return default data
  } else if (state) {
    if (key === 'price') {
      return state.filterData.primaryOptions.price
    } else if (key === 'mileage') {
      return state.filterData.primaryOptions.mileage
    } else if (key === 'options') {
      return {}
    } else {
      return ''
    }
  } else {
    return null
  }
}

function valueExistsInOption(value: string, key: string, type: string): boolean {
  if (type === 'primary') {
    // Check for primary filters
    if (key === 'brand') {
      const selectableBrands = state.filterData.primaryOptions.brands.map((item: any) => item.id)
      return selectableBrands.includes(value)
    } else if (key === 'model') {
      const selectedBrand = state.selectedBrand
      const selectableModels = state.filterData.primaryOptions.brands
        .filter((item: any) => item.id === selectedBrand && item.models.length)[0]
        .models.map((item: any) => item.id)
      return selectableModels && selectableModels.includes(value)
    }
  } else if (type === 'other') {
    // Check for other filters
    const selectableOptions = state.filterData.otherOptions
      .filter((item: any) => item.id === key)[0]
      ?.options.map((item: any) => item.id)
    return selectableOptions && selectableOptions.includes(value)
  }
  return false
}

function saveData() {
  // Save serialized data in localStorage
  sessionStorage.setItem(
    FILTERDATA_KEY,
    JSON.stringify({
      brand: state.selectedBrand,
      model: state.selectedModel,
      price: state.selectedPrice,
      mileage: state.selectedMileage,
      options: state.selectedOptions,
      carselexy: state.isCarselexy,
    })
  )
}

function resetData() {
  // Remove serialized data from localStorage
  sessionStorage.removeItem(FILTERDATA_KEY)
}

function filterAllowedQueryParams(query?: any) {
  if (!query) return null

  const result: any = {}
  const allowedQueryParams = [
    'brand',
    'model',
    'body',
    'fuelType',
    'transmission',
    'buildYear',
    'carselexy',
    'buildyear',
    'fueltype',
  ]

  for (const [key, value] of Object.entries(query)) {
    if (allowedQueryParams.includes(key)) {
      result[key] = value
    }
  }

  return Object.entries(result).length > 0 ? result : null
}

function updateQueryParamsUrl() {
  const filters = {
    brand: state.selectedBrand ? state.selectedBrand : null,
    model: state.selectedModel ? state.selectedModel : null,
    options: state.selectedOptions ? state.selectedOptions : null,
    isCarselexy: state.isCarselexy,
  }
  const queryParams = {}
  for (const [key, value] of Object.entries(filters)) {
    if (value) {
      if (key === 'options') {
        for (const [k, v] of Object.entries(value)) {
          // @ts-ignore
          queryParams[k] = v.join('_')
        }
      }
      if (key === 'brand' || key === 'model' || key === 'isCarselexy') {
        // @ts-ignore
        queryParams[key] = value
      }
    }
  }
  const params = new URLSearchParams(queryParams)
  if (params.toString()) {
    history.replaceState(null, document.title, '?' + params.toString())
  }
}

function removeQueryParamsFromUrl() {
  history.replaceState(null, document.title, window.location.pathname)
}

// initial state
const state = {
  loaded: false,
  filterModalActive: false,
  filterData: new Filters(),
  selectedBrand: '',
  selectedModel: '',
  isCarselexy: false,
  selectedPrice: {},
  selectedMileage: {},
  selectedOptions: {},
  resetSliders: false,
  selectedFilters: [],
}

// getters
const getters = {
  all: (state: any) => {
    return state.filterData
  },

  checkedLabels: (state: any) => {
    // Get all checked options for the tag-labels above results
    const checked: String[] = []
    let enhancedOption: any = {}

    // Loop all possible options
    state.filterData.otherOptions.forEach((cat: any) => {
      cat.options.forEach((option: any) => {
        // If the option is checked, add it to the checked array
        if (cat.id in state.selectedOptions && state.selectedOptions[cat.id].includes(option.id)) {
          // Build up option object
          enhancedOption = {}
          enhancedOption = {
            id: option.id,
            label: option.label,
            parentCat: {
              id: cat.id,
              label: cat.label,
            },
          }
          checked.push(enhancedOption)
        }
      })
    })
    return checked
  },

  primaryOptions: (state: any) => {
    return state.filterData.primaryOptions
  },

  brands: (state: any) => {
    const brands = [...state.filterData.primaryOptions.brands]
    const collator = new Intl.Collator()
    const sortedBrands = brands.sort((a: any, b: any) => {
      return collator.compare(a.id, b.id)
    })
    return sortedBrands
  },

  models: (state: any, getters: any, rootState: any, rootGetters: any) => {
    if (state.selectedBrand) {
      const brandObject = state.filterData.primaryOptions.brands.find((brand: any) => brand.id === state.selectedBrand)
      if (brandObject) {
        const models: any = []
        brandObject.models.forEach((m: any) => {
          const id = m.id.replaceAll('&quot;', '"')
          const label = m.label.replaceAll('&quot;', '"')
          let baseModel = m.id.split(' ')[0]
          if (baseModel.startsWith('e-')) baseModel = baseModel.replace('e-', '')
          if (rootState.settings.general.mergeModels) {
            if (!models.some((m: any) => m.label.startsWith(baseModel))) {
              models.push({ id, label: label.split(' ')[0] })
            }
          } else {
            models.push({ id, label })
          }
        })
        return models
      } else {
        return []
      }
    } else {
      return []
    }
  },
}

// mutations
const mutations = {
  setFromAPI: (state: any, data: any) => {
    const filterData = data.filterData
    const query = filterAllowedQueryParams(data.query)
    state.filterData = filterData
    state.selectedBrand = getData('brand', query)
    state.selectedModel = getData('model', query)
    state.selectedPrice = getData('price', query)
    state.selectedMileage = getData('mileage', query)
    state.selectedOptions = getData('options', query)
    state.isCarselexy = getData('carselexy', query) === '1' ? true : false
    state.loaded = true
  },

  updateOption: (state: any, options: any) => {
    // Option is checked
    if (options.checked) {
      // Check if the object key from category already exists
      // If not, declare it as an empty array
      if (!(options.catId in state.selectedOptions)) {
        Vue.set(state.selectedOptions, options.catId, [])
      }

      // Check if value is already in the array
      // If not, add it
      if (!state.selectedOptions[options.catId].includes(options.optionId)) {
        state.selectedOptions[options.catId].push(options.optionId)
      }

      // Option is unchecked
    } else {
      // Check if the value exists in the category object
      // If it is, find the index and remove it
      if (options.catId in state.selectedOptions && state.selectedOptions[options.catId].includes(options.optionId)) {
        state.selectedOptions[options.catId].splice(state.selectedOptions[options.catId].indexOf(options.optionId), 1)
      }

      // Check if the category object is empty now
      // If it is, remove it from the main object
      if (state.selectedOptions[options.catId].length === 0) {
        delete state.selectedOptions[options.catId]
      }
    }
    saveData()
    updateQueryParamsUrl()
  },

  // Update selected mileage
  updateMileage: (state: any, mileage: { min: string; max: string }) => {
    state.selectedMileage = {
      min: parseFloat(mileage.min),
      max: parseFloat(mileage.max),
    }
    saveData()
    updateQueryParamsUrl()
  },

  // Update selected price
  updatePrice: (state: any, price: { min: string; max: string }) => {
    state.selectedPrice = {
      min: parseFloat(price.min),
      max: parseFloat(price.max),
    }
    saveData()
    updateQueryParamsUrl()
  },

  // Update selected brand
  updateBrand: (state: any, brand: string) => {
    state.selectedBrand = brand
    state.selectedModel = ''
    saveData()
    updateQueryParamsUrl()
  },

  // Update selected model
  updateModel: (state: any, model: string) => {
    state.selectedModel = model
    saveData()
    updateQueryParamsUrl()
  },
  // Update CarSelexy
  updateCarSelexy: (state: any, value: boolean) => {
    state.isCarselexy = value
    saveData()
    updateQueryParamsUrl()
  },

  updateFilterModal: (state: any, value: boolean) => {
    state.filterModalActive = value
  },

  // Reset all selected filters and remove from localStorage
  resetAll: () => {
    state.selectedOptions = {}
    state.selectedBrand = ''
    state.selectedModel = ''
    state.selectedMileage = state.filterData.primaryOptions.mileage
    state.selectedPrice = state.filterData.primaryOptions.price
    state.resetSliders = !state.resetSliders
    removeQueryParamsFromUrl()
    resetData()
  },
}

export default {
  namespaced: true,
  state,
  getters,
  mutations,
}
